import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";

import { Input } from "@/components/ui/input";

import { getSoldCostMonthTie, type SoldCostsEntity } from "./api";
import { COPY, messageFor, money, TIE_FLOOR, tieMonth } from "./format";
import { soldTieLabel, soldTieReason } from "./pnlTieModel";

const ENTITIES: readonly SoldCostsEntity[] = ["LTD", "OU", "LLC"];

function Amount({ value, currency, dp }: { value: string | null; currency: string; dp?: number }) {
  return <td className="py-1 pl-4 text-right tabular-nums">{money(value, currency, dp)}</td>;
}

function SoldCostsTieCompany({ entity, month }: { entity: SoldCostsEntity; month: string }) {
  const query = useQuery({
    queryKey: ["pcogs", "sold-costs", "tie", entity, `${month}-01`],
    queryFn: () => getSoldCostMonthTie(entity, `${month}-01`),
  });
  const data = query.data?.data;
  const caption = data ? `${entity} (${data.currency})` : entity;

  if (query.isPending) return <table className="w-full text-sm"><caption className="pb-2 text-left font-semibold">{caption}</caption><tbody><tr><td>Loading…</td></tr></tbody></table>;
  if (query.error) return <table className="w-full text-sm"><caption className="pb-2 text-left font-semibold">{caption}</caption><tbody><tr><td role="alert">{messageFor(query.error)}</td></tr></tbody></table>;
  if (!data) return null;
  if (data.reason !== null) return <table className="w-full text-sm"><caption className="pb-2 text-left font-semibold">{caption}</caption><tbody><tr><td>{soldTieReason(data.reason)}</td></tr></tbody></table>;

  return <div className="flex flex-col gap-2">
    <table className="w-full text-sm">
      <caption className="pb-2 text-left font-semibold">{caption}</caption>
      <tbody>
        <tr><th scope="row" className="py-1 text-left font-normal">Xero P&amp;L (live)</th><Amount value={data.xero_live} currency={data.currency} /></tr>
        <tr><th scope="row" className="py-1 text-left font-normal">Shown on Sold Costs (the SKU list for the month)</th><Amount value={data.shown} currency={data.currency} /></tr>
        {data.lines.map((line, index) => {
          const named = soldTieLabel(line);
          return <tr key={`${line.kind}-${index}`}>
            <th scope="row" className="py-1 text-left font-normal">{named.label}{named.note && <span className="block text-xs text-ks-grey-1">{named.note}</span>}</th>
            <Amount value={line.amount} currency={data.currency} />
          </tr>;
        })}
        <tr><th scope="row" className="py-1 text-left font-semibold">Unexplained</th><Amount value={data.unexplained} currency={data.currency} dp={4} /></tr>
        <tr><td colSpan={2} className="pt-2 font-semibold">{data.ties ? "Ties to the P&L" : `Does not tie: ${money(data.unexplained, data.currency, 4)} unexplained`}</td></tr>
      </tbody>
    </table>
    {data.xero_reloading && <p className="text-sm text-ks-grey-1">Xero is still reloading this month, so these figures can still move.</p>}
    {data.typed_only.length > 0 && <p className="text-sm text-ks-grey-1">Not re-checked against live Xero (typed in the close): {data.typed_only.join(", ")}</p>}
  </div>;
}

export function SoldCostsTieView() {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const today = new Date();
  const thisMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  const month = tieMonth(search.sc_tie_month, thisMonth);
  const max = tieMonth(undefined, thisMonth);

  return <div className="flex flex-col gap-4">
    <p className="text-sm text-ks-grey-1">{COPY.tieHeader}</p>
    <label className="flex max-w-xs flex-col text-sm">Month<Input data-nonmutating type="month" min={TIE_FLOOR} max={max} value={month} onChange={(event) => void navigate({ to: ".", search: { ...search, sc_tie_month: event.target.value }, replace: true })} /></label>
    {ENTITIES.map((entity) => <SoldCostsTieCompany key={entity} entity={entity} month={month} />)}
  </div>;
}
